"use client";

import { Link } from "@/i18n/routing";
import { Button, Pagination } from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslations } from "next-intl";
import { TournamentGrid } from "../tournament";
import { TournamentWithDetails } from "../tournament/TournamentGrid";

interface HomePageClientProps {
  tournaments: TournamentWithDetails[];
  searchParams?: { [key: string]: string | string[] | undefined };
}

const TOURNAMENTS_PER_PAGE = 12;

export function HomePageClient({
  tournaments,
  searchParams,
}: HomePageClientProps) {
  const t = useTranslations();

  const currentPage = Number(searchParams?.page) || 1;
  const totalPages = Math.ceil(tournaments.length / TOURNAMENTS_PER_PAGE);
  const startIndex = (currentPage - 1) * TOURNAMENTS_PER_PAGE;
  const currentTournaments = tournaments.slice(
    startIndex,
    startIndex + TOURNAMENTS_PER_PAGE,
  );

  return (
    <div className="relative overflow-visible">
      <div
        className="absolute inset-x-0 top-0 h-[100vh] pointer-events-none overflow-hidden motion-reduce:hidden z-0"
        aria-hidden="true"
      >
        <div className="particle particle-1" />
        <div className="particle particle-2" />
        <div className="particle particle-3" />
        <div className="particle particle-4" />
        <div className="particle particle-5" />
        <div className="particle particle-6" />
        <div className="particle particle-7" />
        <div className="particle particle-8" />
      </div>

      {/* Hero Section */}
      <section className="relative z-10 mx-auto flex max-w-7xl flex-col items-center justify-center px-4 pt-12 md:pt-20 pb-12 md:pb-20">
        <div
          className="relative mb-8 md:mb-12 animate-fade-in-up"
          aria-hidden="true"
        >
          <div className="relative">
            <div className="absolute inset-0 -m-4 md:-m-6 rounded-full bg-gradient-to-b from-[var(--gold)]/20 to-transparent blur-xl animate-pulse-slow motion-reduce:animate-none" />

            <div className="relative animate-float motion-reduce:animate-none w-[120px] h-[120px] md:w-[180px] md:h-[180px] lg:w-[220px] lg:h-[220px] flex items-center justify-center">
              <Icon
                icon="game-icons:stone-tower"
                className="text-[120px] md:text-[180px] lg:text-[220px] text-[var(--gold)] drop-shadow-[0_0_30px_rgba(255,215,0,0.4)]"
              />
            </div>

            <div className="absolute inset-0 animate-spin-slow motion-reduce:hidden">
              <div className="absolute top-0 left-1/2 w-2 h-2 -translate-x-1/2 -translate-y-4 rounded-full bg-[var(--gold)] shadow-[0_0_10px_var(--gold)]" />
            </div>
            <div className="absolute inset-0 animate-spin-slow-reverse motion-reduce:hidden">
              <div className="absolute bottom-0 left-1/2 w-1.5 h-1.5 -translate-x-1/2 translate-y-4 rounded-full bg-amber-300 shadow-[0_0_8px_#fcd34d]" />
            </div>
          </div>
        </div>

        <span className="mb-3 md:mb-4 font-cinzel text-xs md:text-sm font-semibold uppercase tracking-[0.2em] text-[var(--gold)] animate-stagger-1">
          {t("home.pretitle")}
        </span>

        <h1 className="mb-4 md:mb-6 font-cinzel text-4xl md:text-6xl lg:text-7xl font-black tracking-wide text-center animate-stagger-2">
          <span className="bg-gradient-to-b from-white via-white to-[var(--gold)] bg-clip-text text-transparent drop-shadow-[0_2px_20px_rgba(255,215,0,0.3)]">
            {t("home.title")}
          </span>
        </h1>

        <p className="max-w-xl md:max-w-2xl text-center text-base md:text-lg lg:text-xl text-[var(--text-secondary)] leading-relaxed mb-10 md:mb-14 px-4 animate-stagger-3">
          {t("home.description")}
        </p>

        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-4 sm:gap-6 animate-stagger-4 justify-center">
          <Button
            as="a"
            href="#tournaments"
            size="lg"
            className="group relative overflow-hidden bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-black font-cinzel font-bold text-base md:text-lg px-8 md:px-10 py-6 md:py-7 shadow-[0_0_30px_rgba(255,215,0,0.3)] hover:shadow-[0_0_50px_rgba(255,215,0,0.5)] transition-[background,box-shadow] duration-300"
            startContent={
              <Icon
                icon="solar:cup-star-bold"
                className="text-xl md:text-2xl group-hover:animate-bounce"
              />
            }
          >
            <span className="relative z-10">
              {t("home.browseTournaments")}
            </span>
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
          </Button>

          <Button
            as={Link}
            href="/start"
            size="lg"
            variant="bordered"
            className="group font-cinzel font-bold text-base md:text-lg px-8 md:px-10 py-6 md:py-7 border-2 border-[var(--gold)]/50 hover:border-[var(--gold)] text-[var(--gold)] hover:bg-[var(--gold)]/10 transition-colors duration-300"
            startContent={
              <Icon
                icon="solar:add-circle-bold"
                className="text-xl md:text-2xl group-hover:rotate-90 transition-transform duration-300"
              />
            }
          >
            {t("home.createTournament")}
          </Button>

          <Button
            as={Link}
            href="/how-to"
            size="lg"
            variant="bordered"
            className="group font-cinzel font-bold text-base md:text-lg px-8 md:px-10 py-6 md:py-7 border-2 border-[var(--secondary)]/50 hover:border-[var(--secondary)] text-[var(--secondary)] hover:bg-[var(--secondary)]/10 transition-colors duration-300"
            startContent={
              <Icon
                icon="game-icons:help"
                className="text-xl md:text-2xl group-hover:rotate-360 transition-transform duration-300"
              />
            }
          >
            {t("home.howTo")}
          </Button>
        </div>
      </section>

      {/* Tournament Grid Section */}
      <section
        id="tournaments"
        className="relative z-10 mx-auto flex max-w-6xl flex-col items-center px-4 pt-12 md:pt-24 pb-16 md:pb-24 scroll-mt-20"
      >
        <h2 className="mb-8 md:mb-10 font-cinzel text-xl md:text-2xl lg:text-3xl font-bold tracking-[0.04em] text-center bg-gradient-to-br from-white to-amber-400 bg-clip-text text-transparent">
          {t("tournaments.hero.activeTournaments")}
        </h2>

        <div className="w-full">
          <TournamentGrid tournaments={currentTournaments} />
        </div>

        {totalPages > 1 && (
          <div className="mt-8 flex justify-center">
            <Pagination total={totalPages} page={currentPage} />
          </div>
        )}

        <Link
          href="/archive"
          className="group mt-10 inline-flex items-center gap-2 font-cinzel text-sm md:text-base font-semibold uppercase tracking-[0.18em] text-[var(--text-secondary)] hover:text-[var(--gold)] transition-colors duration-200"
        >
          <Icon
            icon="solar:archive-linear"
            className="text-lg md:text-xl transition-transform duration-200 group-hover:-translate-x-0.5"
          />
          <span>{t("home.viewArchive")}</span>
          <Icon
            icon="solar:arrow-right-linear"
            className="text-lg md:text-xl transition-transform duration-200 group-hover:translate-x-1"
          />
        </Link>
      </section>

      <style jsx>{`
        @keyframes float-particle {
          0%,
          100% {
            transform: translateY(0) translateX(0);
            opacity: 0.2;
          }
          25% {
            transform: translateY(-20px) translateX(10px);
            opacity: 0.6;
          }
          50% {
            transform: translateY(-40px) translateX(-5px);
            opacity: 0.4;
          }
          75% {
            transform: translateY(-20px) translateX(-10px);
            opacity: 0.6;
          }
        }

        @keyframes float {
          0%,
          100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-12px);
          }
        }

        @keyframes pulse-slow {
          0%,
          100% {
            opacity: 0.6;
          }
          50% {
            opacity: 1;
          }
        }

        @keyframes spin-slow {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .animate-float {
          animation: float 4s ease-in-out infinite;
        }

        .animate-pulse-slow {
          animation: pulse-slow 3s ease-in-out infinite;
        }

        .animate-spin-slow {
          animation: spin-slow 8s linear infinite;
        }

        .animate-spin-slow-reverse {
          animation: spin-slow 12s linear infinite reverse;
        }

        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease-out forwards;
        }

        .animate-fade-in {
          animation: fade-in 0.8s ease-out forwards;
        }

        .animate-stagger-1 {
          opacity: 0;
          animation: fade-in-up 0.8s ease-out 0.2s forwards;
        }
        .animate-stagger-2 {
          opacity: 0;
          animation: fade-in-up 0.8s ease-out 0.3s forwards;
        }
        .animate-stagger-3 {
          opacity: 0;
          animation: fade-in-up 0.8s ease-out 0.4s forwards;
        }
        .animate-stagger-4 {
          opacity: 0;
          animation: fade-in-up 0.8s ease-out 0.5s forwards;
        }

        @media (prefers-reduced-motion: reduce) {
          .animate-stagger-1,
          .animate-stagger-2,
          .animate-stagger-3,
          .animate-stagger-4,
          .animate-fade-in-up,
          .animate-fade-in {
            animation: none;
            opacity: 1;
          }
        }

        .particle {
          position: absolute;
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: var(--gold);
          opacity: 0.6;
          box-shadow: 0 0 8px rgba(255, 215, 0, 0.35);
          transform: translateZ(0);
        }

        .particle-1 {
          left: 10%;
          top: 15%;
          animation: float-particle 10s ease-in-out infinite;
        }
        .particle-2 {
          left: 85%;
          top: 20%;
          animation: float-particle 12s ease-in-out infinite 0.5s;
        }
        .particle-3 {
          left: 25%;
          top: 60%;
          animation: float-particle 14s ease-in-out infinite 1s;
        }
        .particle-4 {
          left: 70%;
          top: 75%;
          animation: float-particle 11s ease-in-out infinite 1.5s;
        }
        .particle-5 {
          left: 45%;
          top: 35%;
          animation: float-particle 13s ease-in-out infinite 2s;
        }
        .particle-6 {
          left: 90%;
          top: 50%;
          animation: float-particle 9s ease-in-out infinite 2.5s;
        }
        .particle-7 {
          left: 5%;
          top: 80%;
          animation: float-particle 15s ease-in-out infinite 3s;
        }
        .particle-8 {
          left: 60%;
          top: 10%;
          animation: float-particle 11s ease-in-out infinite 3.5s;
        }

        @media (prefers-reduced-motion: reduce) {
          .particle {
            display: none;
          }
        }
      `}</style>
    </div>
  );
}
